import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Eye, EyeOff, Shield, Package } from "lucide-react";

export default function Settings() {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [merchEnabled, setMerchEnabled] = useState(false);
  const [merchLoading, setMerchLoading] = useState(false);

  useEffect(() => {
    api.get("/settings/merch").then(r => setMerchEnabled(r.data.merch_enabled)).catch(() => {});
  }, []);

  const toggleMerch = async () => {
    setMerchLoading(true);
    try {
      const { data } = await api.put("/settings/merch", { merch_enabled: !merchEnabled });
      setMerchEnabled(data.merch_enabled);
      toast.success(data.merch_enabled ? "Merchandise enabled" : "Merchandise disabled");
    } catch { toast.error("Failed to update setting"); }
    finally { setMerchLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (newPw !== confirm) { setError("New passwords do not match"); return; }
    if (!/[A-Z]/.test(newPw) || !/[0-9]/.test(newPw)) { setError("Password must include uppercase letter and number"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPw });
      toast.success("Password changed successfully");
      setCurrent(""); setNewPw(""); setConfirm("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-heading text-3xl mb-1">SETTINGS</h1>
      <p className="text-white/40 text-sm mb-8">Manage your admin account</p>

      <div className="max-w-md space-y-6">
        {/* Merch Toggle */}
        <div className="bg-[#111111] border border-white/5 p-6 rounded">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-xl">MERCHANDISE</h2>
          </div>
          <p className="text-white/40 text-xs mb-4">Enable or disable physical merchandise purchases globally. When OFF, all merch/clothing/accessories show "Coming Soon" and cannot be purchased.</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">{merchEnabled ? "Merch is LIVE" : "Merch is OFF"}</p>
              <p className="text-[10px] text-white/30 mt-0.5">{merchEnabled ? "Customers can purchase physical products" : "Physical products show Coming Soon"}</p>
            </div>
            <button
              data-testid="merch-toggle-btn"
              onClick={toggleMerch}
              disabled={merchLoading}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${merchEnabled ? "bg-[#D4AF37]" : "bg-white/10"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${merchEnabled ? "left-[26px]" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="bg-[#111111] border border-white/5 p-6 rounded">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-xl">CHANGE PASSWORD</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 p-3 rounded">{error}</div>}

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Current Password</label>
              <div className="relative">
                <input data-testid="current-password-input" type={showCurrent ? "text" : "password"} value={current} onChange={e => setCurrent(e.target.value)} required
                  className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 pr-10 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">New Password</label>
              <div className="relative">
                <input data-testid="new-password-input" type={showNew ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 number" required
                  className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 pr-10 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20" />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Confirm New Password</label>
              <input data-testid="confirm-password-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
            </div>

            <Button data-testid="change-password-btn" type="submit" disabled={loading} className="bg-[#D4AF37] text-black hover:bg-white text-sm w-full py-3 h-auto">
              <Save className="w-4 h-4 mr-2" /> {loading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
